import { Injectable } from '@angular/core';
import {
  AuthChangeEvent,
  AuthError,
  Session,
  SupabaseClient,
} from '@supabase/supabase-js';

import {
  getSupabaseClient,
  oauthRedirectUrl,
} from '../../../core/supabase/supabase-client';
import { Profile, profileFromJson } from '../models/profile';

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private readonly supabase: SupabaseClient = getSupabaseClient();

  onAuthStateChange(
    callback: (event: AuthChangeEvent, session: Session | null) => void,
  ): { data: { subscription: { unsubscribe: () => void } } } {
    return this.supabase.auth.onAuthStateChange(callback);
  }

  async getSession(): Promise<Session | null> {
    const {
      data: { session },
    } = await this.supabase.auth.getSession();
    return session;
  }

  async getCurrentUserId(): Promise<string | null> {
    const session = await this.getSession();
    return session?.user.id ?? null;
  }

  detectRecoveryFromUri(): boolean {
    const uri = new URL(window.location.href);
    const type =
      uri.searchParams.get('type') ??
      new URLSearchParams(uri.hash.replace(/^#/, '')).get('type');
    return type === 'recovery';
  }

  async signInWithEmail(email: string, password: string): Promise<void> {
    const { error } = await this.supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) {
      throw error;
    }
  }

  async signUpWithEmail(email: string, password: string): Promise<void> {
    const { error } = await this.supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { emailRedirectTo: oauthRedirectUrl() },
    });
    if (error) {
      throw error;
    }
  }

  async signInWithGoogle(): Promise<void> {
    const { error } = await this.supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: oauthRedirectUrl() },
    });
    if (error) {
      throw error;
    }
  }

  async resetPasswordForEmail(email: string): Promise<void> {
    const { error } = await this.supabase.auth.resetPasswordForEmail(
      email.trim(),
      { redirectTo: oauthRedirectUrl() },
    );
    if (error) {
      throw error;
    }
  }

  async updatePassword(newPassword: string): Promise<void> {
    const { error } = await this.supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) {
      throw error;
    }
  }

  async signOut(): Promise<void> {
    const { error } = await this.supabase.auth.signOut();
    if (error) {
      throw error;
    }
  }

  async fetchCurrentProfile(userId?: string): Promise<Profile | null> {
    const id = userId ?? (await this.getCurrentUserId());
    if (!id) {
      return null;
    }

    const { data, error } = await this.supabase
      .from('profiles')
      .select()
      .eq('id', id)
      .maybeSingle();

    if (error) {
      throw error;
    }
    if (!data) {
      return null;
    }

    return profileFromJson(data as Record<string, unknown>);
  }

  async setUsername(username: string): Promise<Profile> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new AuthError('Not signed in', 401, 'not_signed_in');
    }

    const normalized = username.trim().toLowerCase();
    const { data, error } = await this.supabase
      .from('profiles')
      .update({ username: normalized })
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return profileFromJson(data as Record<string, unknown>);
  }

  async exchangeCodeForSession(code: string): Promise<void> {
    const { error } = await this.supabase.auth.exchangeCodeForSession(code);
    if (error) {
      throw error;
    }
  }
}
